import { useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { Database } from '../lib/supabase';
import type { SupabaseClient } from '@supabase/supabase-js';
type Interviewer = Database['public']['Tables']['interviewers']['Row'];
type NewInterviewer = Omit<Interviewer, 'id' | 'created_at' | 'interviews_count' | 'last_active'>;

type FeedbackInput = Database['public']['Tables']['feedback']['Insert'];

export function useSupabase() {
  // Return both the client and the utility functions
  const client: SupabaseClient = supabase;

  const fetchInterviewers = useCallback(async () => {
    const { data, error } = await supabase
      .from('interviewers')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  }, []);

  const createInterviewer = useCallback(async (interviewer: NewInterviewer) => {
    if (!interviewer.name || !interviewer.company || !interviewer.url) {
      throw new Error('Missing required fields: name, company, and URL are required');
    }

    const { data, error } = await supabase
      .from('interviewers')
      .insert([interviewer])
      .select()
      .single();

    if (error) {
      console.error('Supabase error:', error);
      if (error.code === '23505') {
        throw new Error('An interviewer with this URL already exists');
      } else if (error.code === '23514') {
        throw new Error('Invalid input - please check all required fields');
      } else {
        throw new Error(`Database error: ${error.message}`);
      }
    }

    if (!data) {
      throw new Error('No data returned from server');
    }

    return data;
  }, []);

  const updateInterviewer = useCallback(async (id: number, updates: Partial<NewInterviewer>) => {
    const { data, error } = await supabase
      .from('interviewers')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }, []);

  const deleteInterviewer = useCallback(async (id: number) => {
    const { error } = await supabase
      .from('interviewers')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }, []);

  const getInterviewer = useCallback(async (id: number) => {
    const { data, error } = await supabase
      .from('interviewers')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  }, []);

  const updateLastActive = useCallback(async (id: number) => {
    console.log('updateLastActive called with id:', id);
    
    try {
      // First, verify the interviewer exists
      const { data: interviewer, error: fetchError } = await supabase
        .from('interviewers')
        .select('id, interviews_count')
        .eq('id', id)
        .single();

      if (fetchError) {
        console.error('Error fetching interviewer:', fetchError);
        throw new Error('Failed to verify interviewer exists');
      }

      if (!interviewer) {
        throw new Error('Interviewer not found');
      }

      // Then update both last_active and interviews_count
      const { data: updated, error: updateError } = await supabase
        .from('interviewers')
        .update({ 
          last_active: new Date().toISOString(),
          interviews_count: interviewer.interviews_count + 1
        })
        .eq('id', id)
        .select()
        .single();

      if (updateError) {
        console.error('Error updating interviewer:', updateError);
        throw new Error('Failed to update interview statistics');
      }

      console.log('Successfully updated last_active and interviews_count:', updated);
      return updated;
    } catch (error) {
      console.error('Error in updateLastActive:', error);
      throw error instanceof Error ? error : new Error('An unexpected error occurred');
    }
  }, []);

  const submitFeedback = useCallback(async (feedback: FeedbackInput) => {
    if (!feedback.rating) {
      throw new Error('Rating is required');
    }

    const { data, error } = await supabase
      .from('feedback')
      .insert([feedback])
      .select()
      .single();

    if (error) {
      console.error('Supabase feedback submission error:', error);
      if (error.code === '23503') {
        throw new Error('Invalid interviewer ID');
      } else if (error.code === '23514') {
        throw new Error('Invalid input - please check all required fields');
      } else {
        throw new Error(error.message || 'Failed to submit feedback');
      }
    }

    return data;
  }, []);

  return {
    client,
    fetchInterviewers,
    createInterviewer,
    updateInterviewer,
    deleteInterviewer,
    getInterviewer,
    submitFeedback,
    updateLastActive,
  };
}